import type { BrandSlug } from '../shared/brand-slug';
import type { OrgId } from '../shared/ids';
import { type Coverage, emptyCoverage } from './coverage';

export type AllowedBrandBuckets = {
  readonly own: ReadonlySet<BrandSlug>;
  readonly competitor: ReadonlySet<BrandSlug>;
  readonly other: ReadonlySet<BrandSlug>;
};

export type Entitlement = {
  readonly kind: 'entitlement';
  readonly orgId: OrgId;
  readonly allowed: AllowedBrandBuckets;
  readonly coverage: Coverage;
};

export function emptyEntitlement(orgId: OrgId): Entitlement {
  return {
    kind: 'entitlement',
    orgId,
    allowed: { own: new Set(), competitor: new Set(), other: new Set() },
    coverage: emptyCoverage(orgId),
  };
}

export type BrandBucketKind = 'own' | 'comp' | 'other';

export type BrandAccessPolicy = {
  readonly flat: ReadonlySet<BrandSlug>;
  readonly canViewBrand: (slug: BrandSlug) => boolean;
  readonly scopeOf: (slug: BrandSlug) => BrandBucketKind | null;
};

export function brandAccessPolicyFor(entitlement: Entitlement): BrandAccessPolicy {
  const own = entitlement.allowed.own;
  const comp = entitlement.allowed.competitor;
  const other = entitlement.allowed.other;
  const flat = new Set<BrandSlug>();
  for (const s of own) flat.add(s);
  for (const s of comp) flat.add(s);
  for (const s of other) flat.add(s);
  return {
    flat,
    canViewBrand: (slug) => flat.has(slug),
    scopeOf: (slug) =>
      own.has(slug) ? 'own' : comp.has(slug) ? 'comp' : other.has(slug) ? 'other' : null,
  };
}
